"use client";

import Link from "next/link";

import { cn } from "@/lib/utils";
import type { IPageAuthor } from "@/types/author";
import {
  PREVIEW_AUTHOR,
  formatArticlesPublished,
  isBlogPageType,
  resolveAuthorProfileHref,
} from "@/utils/authorWidget";

interface AuthorCard1Props {
  data?: any;
  /** Author linked to the page (server: `page.author`), injected by NewPage. */
  author?: IPageAuthor | null;
  /** Page type, injected by NewPage. Anything but "blog" hides the widget. */
  pageCategory?: string;
  isCmsPreview?: boolean;
}

const POPPINS = "var(--font-poppins), Poppins, sans-serif";

const isOn = (v: unknown) => v === true || v === 1 || v === "1";

/**
 * Author Card 1 (section 3003 / variant 3000) — Atlas 13115:32413 (desktop) and
 * 13116:23094 (mobile). A compact bio card: avatar, author name linking to their
 * profile, article count and bio.
 *
 * RENDERS ONLY ON A BLOG PAGE WITH AN AUTHOR LINKED. Everything it shows comes
 * from that author, so there is no content config — only colours and whether the
 * name is a link.
 *
 * Layout note: one grid drives both breakpoints. The bio spans both columns on
 * mobile (sitting under the avatar) and only the second column on desktop (sitting
 * beside it), which is the sole structural difference between the two frames.
 */
export default function AuthorCard1({
  data,
  author: pageAuthor,
  pageCategory,
  isCmsPreview,
}: AuthorCard1Props) {
  const row = data?.data ?? data ?? {};

  // The CMS widget preview has no page behind it, so it falls back to a stand-in
  // author purely so the editor can see the colours they are picking.
  const author: IPageAuthor | null = pageAuthor ?? (isCmsPreview ? PREVIEW_AUTHOR : null);

  if (!isCmsPreview && !isBlogPageType(pageCategory)) return null;
  if (!author?.name) return null;

  const backgroundColor = row.background_color || "#FFFFFF";
  const nameColor = row.author_name_color || "#342B7C";
  const metaColor = row.meta_text_color || "rgba(0,0,0,0.6)";
  const descriptionColor = row.description_text_color || "#000000";
  const showAuthorLink = isOn(row.show_author_link ?? true);

  const articlesLine = formatArticlesPublished(author.articles_published);
  const description = String(author.description ?? "").trim();
  const avatarUrl = String(author.profile_image_url ?? "").trim();
  const profileHref = resolveAuthorProfileHref(author);

  const nameClass =
    "w-full break-words text-[14px] font-semibold leading-normal tab:text-[16px]";

  return (
    <div
      className="grid w-full grid-cols-[44px_1fr] gap-x-[14px] gap-y-[14px] rounded-[10px] p-[14px] tab:gap-y-[10px]"
      style={{ backgroundColor, fontFamily: POPPINS }}
    >
      <div className="size-[44px] self-center overflow-hidden rounded-[10px] border border-solid border-white shadow-[0px_2px_14px_0px_rgba(0,0,0,0.15)] tab:self-start">
        {avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatarUrl}
            alt={author.name}
            className="size-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex size-full items-center justify-center bg-black/5 text-[16px] font-semibold uppercase"
            style={{ color: nameColor }}
          >
            {author.name.trim().charAt(0)}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-[2px] self-center tab:self-start">
        {showAuthorLink ? (
          <Link
            href={profileHref}
            className={cn(
              nameClass,
              "underline decoration-solid [text-underline-position:from-font]",
            )}
            style={{ color: nameColor }}
          >
            {author.name}
          </Link>
        ) : (
          <span className={nameClass} style={{ color: nameColor }}>
            {author.name}
          </span>
        )}

        {articlesLine ? (
          <p
            className="w-full break-words text-[10px] leading-normal tab:text-[12px]"
            style={{ color: metaColor }}
          >
            {articlesLine}
          </p>
        ) : null}
      </div>

      {description ? (
        <p
          className="col-span-2 min-w-0 break-words text-[12px] leading-normal tab:col-span-1 tab:col-start-2 tab:text-[14px]"
          style={{ color: descriptionColor }}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}
